<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white border-b border-gray-200 shadow-sm">
      <div class="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
        <router-link
          to="/"
          class="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <span class="text-xl font-bold text-gray-900">FileShare</span>
        </router-link>
      </div>
    </header>

    <main class="max-w-2xl mx-auto px-4 py-12">

      <!-- Loading State -->
      <div v-if="isLoading" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
        <svg class="w-10 h-10 animate-spin mx-auto text-blue-500 mb-4" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-gray-500">ファイル情報を読み込んでいます…</p>
      </div>

      <!-- Error State -->
      <div v-else-if="errorMessage" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
        <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">ファイルが見つかりません</h2>
        <p class="text-gray-500 mb-6">{{ errorMessage }}</p>
        <router-link
          to="/"
          class="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-medium transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          トップページへ
        </router-link>
      </div>

      <!-- Password Gate -->
      <div v-else-if="requiresPassword && !fileInfo" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <div class="flex flex-col items-center text-center mb-6">
          <div class="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
            <svg class="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 class="text-xl font-bold text-gray-900 mb-1">パスワード保護されたファイル</h1>
          <p class="text-gray-500 text-sm">
            このファイルにアクセスするにはパスワードが必要です
          </p>
          <p v-if="lockedFileInfo?.fileName" class="mt-2 text-sm font-medium text-gray-700 truncate max-w-xs">
            {{ lockedFileInfo.fileName }}
            <span class="font-normal text-gray-400 ml-1">({{ formatSize(lockedFileInfo.fileSize) }})</span>
          </p>
        </div>

        <!-- Password Form -->
        <form @submit.prevent="submitPassword" class="space-y-3">
          <div class="relative">
            <input
              ref="passwordInputRef"
              v-model="enteredPassword"
              :type="showPassword ? 'text' : 'password'"
              placeholder="パスワードを入力"
              :disabled="isVerifying"
              class="w-full border rounded-xl px-4 py-3 text-sm pr-11 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              :class="wrongPassword ? 'border-red-400 bg-red-50' : 'border-gray-300'"
              autofocus
            />
            <button
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              @click="showPassword = !showPassword"
            >
              <svg v-if="showPassword" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>

          <p v-if="wrongPassword" class="text-xs text-red-600 flex items-center gap-1">
            <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            パスワードが正しくありません
          </p>

          <button
            type="submit"
            :disabled="!enteredPassword || isVerifying"
            class="w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
            :class="enteredPassword && !isVerifying
              ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'"
          >
            <svg v-if="isVerifying" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
            </svg>
            {{ isVerifying ? '確認中…' : 'アクセスする' }}
          </button>
        </form>
      </div>

      <!-- File Info Card -->
      <div v-else-if="fileInfo" class="space-y-4">
        <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div class="flex flex-col items-center text-center mb-8">
            <div class="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
              <svg class="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-gray-900 mb-1 break-all">{{ fileInfo.fileName }}</h1>
            <p class="text-gray-500 text-sm">ダウンロード準備完了</p>
          </div>

          <div class="grid grid-cols-2 gap-4 mb-8">
            <div class="bg-gray-50 rounded-xl p-4 text-center">
              <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">サイズ</p>
              <p class="text-lg font-semibold text-gray-800">{{ formatSize(fileInfo.fileSize) }}</p>
            </div>
            <div class="bg-gray-50 rounded-xl p-4 text-center">
              <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">アップロード日時</p>
              <p class="text-sm font-semibold text-gray-800">{{ formatDate(fileInfo.uploadedAt) }}</p>
            </div>
          </div>

          <a
            :href="fileInfo.downloadUrl"
            :download="fileInfo.fileName"
            target="_blank"
            class="flex items-center justify-center gap-3 w-full py-3.5 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors shadow-sm hover:shadow"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            ダウンロード
          </a>

          <p class="text-center text-xs text-gray-400 mt-4">
            ダウンロードリンクの有効期限は 1 時間です
          </p>
        </div>

        <div class="text-center">
          <router-link
            to="/"
            class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            ファイルをアップロードする
          </router-link>
        </div>
      </div>

    </main>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { getDownloadInfo } from '../api.js';

const route = useRoute();

const isLoading = ref(true);
const errorMessage = ref('');
const fileInfo = ref(null);         // set when download is unlocked
const lockedFileInfo = ref(null);   // file name/size shown behind password gate
const requiresPassword = ref(false);

const enteredPassword = ref('');
const showPassword = ref(false);
const isVerifying = ref(false);
const wrongPassword = ref(false);
const passwordInputRef = ref(null);

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('ja-JP', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

async function submitPassword() {
  if (!enteredPassword.value || isVerifying.value) return;
  isVerifying.value = true;
  wrongPassword.value = false;

  try {
    const data = await getDownloadInfo(route.params.token, enteredPassword.value);
    if (data.requiresPassword) {
      // Should not happen (we sent a password), treat as wrong
      wrongPassword.value = true;
    } else {
      fileInfo.value = data;
    }
  } catch (err) {
    if (err.status === 401) {
      wrongPassword.value = true;
      enteredPassword.value = '';
      await nextTick();
      passwordInputRef.value?.focus();
    } else {
      errorMessage.value = err.message;
    }
  } finally {
    isVerifying.value = false;
  }
}

onMounted(async () => {
  const token = route.params.token;
  if (!token) {
    errorMessage.value = '無効な共有リンクです。';
    isLoading.value = false;
    return;
  }

  try {
    const data = await getDownloadInfo(token);

    if (data.requiresPassword) {
      requiresPassword.value = true;
      lockedFileInfo.value = data; // has fileName, fileSize but no downloadUrl
    } else {
      fileInfo.value = data;
    }
  } catch (err) {
    errorMessage.value =
      err.message === 'File not found for the given share token'
        ? 'この共有リンクは無効か、ファイルが削除されています。'
        : `ファイルを読み込めませんでした: ${err.message}`;
  } finally {
    isLoading.value = false;
  }
});
</script>
